import React from 'react';
import { useIntl } from 'react-intl';
import { Button, CircularProgress } from '@mui/material';
import { useOemFormatContext } from '../../hooks/useOemFormatContext';
import { useLatestRemoteCommand } from '../hooks/useLatestRemoteCommand';
import { useRemoteCommandHistory } from '../hooks/useRemoteCommandHistory';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel';
import { RemoteCommandItem } from './RemoteCommandItem';
import { renderQueryState } from './renderQueryState';
import '../remote-commands.scss';

/** Bottom of the list: next-page spinner or error, the observer's sentinel, or the "Load more" fallback. */
const ListFooter = ({ query, scroll, onLoadMore, messages }) => {
	if (query.isFetchingNextPage) {
		return (
			<li className="oem-remote-history-footer" role="status">
				<CircularProgress size={16} />
				{messages['oemDataModal_remote_loadingMore']}
			</li>
		);
	}
	if (query.isFetchNextPageError) {
		return (
			<li className="oem-remote-history-footer">
				<span className="oem-remote-item-error">{messages['oemDataModal_remote_error']}</span>
				<Button variant="text" size="small" onClick={() => query.fetchNextPage()}>
					{messages['oemDataModal_retry']}
				</Button>
			</li>
		);
	}
	if (!query.hasNextPage) return null;
	if (!scroll.supported) {
		return (
			<li className="oem-remote-history-footer">
				<Button variant="text" size="small" onClick={onLoadMore}>
					{messages['oemDataModal_remote_loadMore']}
				</Button>
			</li>
		);
	}
	return <li ref={scroll.sentinelRef} className="oem-remote-history-sentinel" aria-hidden="true" />;
};

/**
 * "Command history" tab of OemDataModal: every command of the vehicle, newest
 * first, in its own scroll container, 25 at a time (infinite scroll). Also
 * keeps the current command polled while active, which refreshes its row here.
 */
export const RemoteCommandHistoryTab = ({ record }) => {
	const { messages } = useIntl();
	const { formatDateTime } = useOemFormatContext();
	useLatestRemoteCommand(record.unitId);
	const { query, commands, loadMore } = useRemoteCommandHistory(record.unitId);
	const scroll = useInfiniteScrollSentinel({ onVisible: loadMore, enabled: Boolean(query.hasNextPage) && !query.isFetchNextPageError });

	const state = renderQueryState(query, messages);
	if (state) return state;
	if (commands.length === 0) {
		return <p className="oem-data-modal-message">{messages['oemDataModal_remote_empty']}</p>;
	}

	return (
		<ul ref={scroll.rootRef} className="oem-remote-history" aria-label={messages['oemDataModal_remote_historyTab']}>
			{commands.map((command) => (
				<RemoteCommandItem key={command.id} as="li" command={command} formatDateTime={formatDateTime} />
			))}
			<ListFooter query={query} scroll={scroll} onLoadMore={loadMore} messages={messages} />
		</ul>
	);
};
